import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { handleAuthCallback } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processingRef = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double processing in React StrictMode
      if (processingRef.current) {
        console.log('🔄 Callback already being processed, skipping...');
        return;
      }
      processingRef.current = true;

      // Small delay to help prevent race conditions
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Check if we already have tokens - if so, just navigate
        const existingToken = localStorage.getItem('access_token');
        if (existingToken) {
          console.log('🔄 Tokens already exist, skipping callback processing');

          // Check profile completion from existing token
          const idToken = localStorage.getItem('id_token');
          if (idToken) {
            try {
              const base64Url = idToken.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(
                atob(base64)
                  .split('')
                  .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                  .join('')
              );
              const userInfo = JSON.parse(jsonPayload);

              const profileComplete = userInfo['custom:profile_completed'] === 'true';
              navigate(profileComplete ? '/dashboard' : '/profile/complete');
              return;
            } catch (tokenError) {
              console.error('Failed to parse existing JWT token:', tokenError);
            }
          }
          navigate('/dashboard');
          return;
        }

        // Check if we have an authorization code to process
        const code = searchParams.get('code');
        if (!code) {
          console.log('🔄 No authorization code found, redirecting to login');
          navigate('/login');
          return;
        }

        // Process the authorization code
        console.log('🔄 Processing authorization code...');
        await handleAuthCallback();

        // After successful callback, check profile completion
        if (process.env.NODE_ENV === 'test') {
          // In test environment, use JWT token check
          const token = localStorage.getItem('id_token');
          if (token) {
            try {
              const base64Url = token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(
                atob(base64)
                  .split('')
                  .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                  .join('')
              );
              const userInfo = JSON.parse(jsonPayload);

              const profileComplete = userInfo['custom:profile_completed'] === 'true';
              navigate(profileComplete ? '/dashboard' : '/profile/complete');
            } catch (tokenError) {
              console.error('Failed to parse JWT token:', tokenError);
              navigate('/dashboard');
            }
          } else {
            navigate('/dashboard');
          }
        } else {
          // In production, check profile completion status from API
          try {
            const { apiClient } = await import('../utils/apiClient');

            // First, try to trigger OAuth callback to create profile if needed
            try {
              const callbackResponse = await apiClient.post('/v1/profile/callback');
              console.log('✅ OAuth callback processed successfully:', callbackResponse?.data);
            } catch (callbackError: any) {
              console.log('⚠️ OAuth callback not needed or failed:', {
                error: callbackError,
                message: callbackError?.response?.data?.error?.message || callbackError?.message,
                status: callbackError?.response?.status
              });
            }

            // Check profile status
            const response = await apiClient.get('/v1/profile/status');
            const profileStatus = response?.data?.data;

            console.log('📊 Profile status check:', {
              response: response?.data,
              profileStatus: profileStatus,
              profileComplete: profileStatus?.profile_complete
            });

            if (!profileStatus?.profile_complete) {
              console.log('🔄 Redirecting to profile completion');
              navigate('/profile/complete');
            } else {
              console.log('🔄 Redirecting to dashboard');
              navigate('/dashboard');
            }
          } catch (profileError: any) {
            console.error('❌ Failed to check profile status:', {
              error: profileError,
              message: profileError?.response?.data?.error?.message || profileError?.message,
              status: profileError?.response?.status,
              url: profileError?.config?.url
            });
            // For new users, assume profile is incomplete
            console.log('🔄 Assuming new user, redirecting to profile completion');
            navigate('/profile/complete');
          }
        }
      } catch (err) {
        console.error('Authentication callback failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [handleAuthCallback, navigate, searchParams]);

  if (isProcessing) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="card-title">Completing Authentication</h5>
                <p className="card-text text-muted">
                  Please wait while we complete your login...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card border-danger">
              <div className="card-body text-center">
                <i className="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
                <h5 className="card-title text-danger">Authentication Failed</h5>
                <p className="card-text">
                  {error}
                </p>
                <div className="mt-3">
                  <button
                    className="btn btn-primary me-2"
                    onClick={() => navigate('/login')}
                  >
                    <i className="fas fa-sign-in-alt me-1"></i>
                    Try Again
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/')}
                  >
                    <i className="fas fa-home me-1"></i>
                    Go Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
